import { memo, useEffect, useMemo, useRef, useState } from "react";
import Toggle from "src/components/Toggle";
import { cn } from "src/lib/cn";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { useSudowriteStore } from "src/stores/sudowriteStore";
import { MultiCheck } from "src/sudokit/components/MultiCheck";
import MenuSelect from "src/sudokit/components/SudoSelect/MenuSelect";
import { Text } from "src/sudokit/components/Text";
import { GET_POV_ABBREVATION, POVS, POV_OPTIONS, extractFirstName, isAutoPOV } from "../../../../../../src/shared/constants";
import { AI_VISIBILITY } from "../../../../../../src/shared/enums";
import { extractTextFromTipTap } from "../../../../../../src/shared/schemas/CharacterSchema";
import { MultiOptionSelect, useSelectItem } from "../../../../sudokit/components/SudoSelect/MultiOptionSelect";

const EMPTY_CHARACTERS = [];

const POVRadioButtons = ({ value, onChange }) => {
  const pov = isAutoPOV(value) ? POVS.AUTO : value;
  useSelectItem(pov, true);

  return (
    <div>
      <div>
        {POV_OPTIONS.map((option) => (
          <div
            key={option.value}
            className={cn(
              "flex items-center gap-2 pb-3 pt-2 px-2.5 mb-2 last:mb-0 text-white bg-app-document-background !border !border-solid duration-150 ease-in-out cursor-pointer rounded-lg",
              value === option.value
                ? "!border-app-primary-cta-high-contrast text-app-primary-cta-high-contrast cursor-default hover:cursor-default"
                : "!border-app-grey-2 text-app-grey-0 hover:text-app-grey-0 hover:!border-app-grey-1 hover:bg-app-grey-4 active:scale-[0.995] hover:scale-[1.005] cursor-pointer",
            )}
            onClick={() => onChange(value === option.value ? POVS.NONE : option.value)}
          >
            {/* Set the value on the parent so the whole box is clickable */}
            <Toggle
              checked={value === option.value}
              set={() => { }}
            />
            <div className="w-[90%]">
              <Text
                size="base"
                weight="medium"
                color={value === option.value ? "primary-cta" : "grey-0"}
                font="inter"
              >
                {option.label}
              </Text>
              <span className="text-left">
                <Text
                  lineHeight="snug"
                  weight="medium"
                  color={value === option.value ? "primary-cta" : "grey-2"}
                  font="inter"
                  size="sm"
                >
                  {option.description}
                </Text>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const POVCharacterSelect = ({ characters = [], selectedCharacters, onChange, oneOrManySelections }) => {
  useSelectItem(selectedCharacters, true);
  const ref = useRef(null);

  // For now, filter out unnamed characters since if the user has many, it's hard to tell which is which.
  const filteredCharacters = characters.filter((character) => character.value !== "" && character.label !== "");

  if (!filteredCharacters || filteredCharacters.length === 0) {
    return (
      <div className="mt-6 px-1">
        <p className="font-inter font-medium text-app-grey-0 mb-2">POV Character{oneOrManySelections === "one" ? "" : "s"}:</p>
        <Text
          size="sm"
          font="inter"
        >
          Add {oneOrManySelections === "one" ? "a named character" : "named characters"} in the Story Bible to write from specific points of view.
        </Text>
      </div>
    );
  }

  if (filteredCharacters.length > 0 && oneOrManySelections === "one") {
    filteredCharacters.splice(0, 0, { value: "", label: "Auto" });

    const handleChange = (value) => {
      if (onChange) {
        onChange(value ? [value] : []);
      }
    };

    return (
      <div className="flex items-center mt-6 mb-2 gap-[8px]">
        <p className="font-inter font-medium text-app-grey-0">POV Character:</p>
        <MenuSelect
          options={filteredCharacters}
          value={selectedCharacters[0] || ""}
          onChange={handleChange}
          placeholder="Select POV Character"
          variant="tertiary"
          collisionBoundarySelector=".sudokit-modal"
          ref={ref}
        />
      </div>
    );
  }

  return (
    <div className="mt-6">
      <p className="font-inter font-medium text-app-grey-0 mb-4">POV Characters:</p>
      <MultiCheck
        options={filteredCharacters}
        value={selectedCharacters}
        onChange={onChange}
        ref={ref}
      />
    </div>
  );
};

export const POVSelectorButton = memo(({ pov, handleManualPOVChange, handleCharacterSelectChange, oneOrManyCharacters = "one", showLabel = true, collisionBoundarySelector = null }) => {
  const features = useSudowriteStore((state) => state.getFeatureFlags)();
  const { hasAccessToPOVAndTenseBeta } = features || {};
  const povCharacters = pov?.characters || [];

  // Don't show POV characters if POV is 3rd person omniscient or Auto
  const [is3rdPersonOmniscient, setIs3rdPersonOmniscient] = useState(pov?.value === POVS.THIRD_PERSON_OMNISCIENT);

  useEffect(() => {
    setIs3rdPersonOmniscient(pov?.value === POVS.THIRD_PERSON_OMNISCIENT);
  }, [pov?.value]);

  const characters = useStoryBibleStore((state) => state.charactersEntities) || EMPTY_CHARACTERS;
  const characterNames = useMemo(
    () =>
      characters
        .filter((character) => character.aiVisibility !== AI_VISIBILITY.HIDDEN)
        .map((character) => ({
          value: character.id,
          label: extractTextFromTipTap(character.name),
        })),
    [characters],
  );

  if (!hasAccessToPOVAndTenseBeta) {
    return null;
  }

  const getPOVSummary = (selectedValues) => {
    // Find the POV value (it will be one of the options values)
    let povValue = selectedValues.find((value) => [...POV_OPTIONS, { value: POVS.AUTO, label: "auto" }].some((option) => option.value === value));

    // Map the POV value to its abbreviated version
    if (povValue) {
      if (isAutoPOV(povValue)) {
        return POVS.AUTO_ABBR;
      } else {
        return GET_POV_ABBREVATION(povValue);
      }
    }

    return null;
  };

  const getCharacterSummary = (selectedValues) => {
    // Get all character values (excluding POV options) and extract first names
    const characterValues = selectedValues
      .flatMap((value) => value.split(",").map((v) => v.trim()))
      .filter((value) => !POV_OPTIONS.some((option) => option.value === value))
      .filter((value) => value !== undefined && value !== "undefined")
      .map((value) => {
        const character = characters.find((character) => character.id === value && character.aiVisibility !== AI_VISIBILITY.HIDDEN);

        if (character) {
          const firstName = extractTextFromTipTap(character.name);
          return extractFirstName(firstName);
        }
        return "";
      })
      .filter((value) => value !== "");

    return characterValues.join(", ") || "Auto";
  };

  return (
    <>
      <MultiOptionSelect
        placeholder="Select POV"
        variant="tertiary"
        summaryFn={getPOVSummary}
        collisionBoundarySelector={collisionBoundarySelector}
      >
        <div className="px-2 pt-2 pb-3">
          <POVRadioButtons
            value={pov?.value || POVS.THIRD_PERSON_LIMITED}
            onChange={handleManualPOVChange}
          />
        </div>
      </MultiOptionSelect>

      {characterNames && !is3rdPersonOmniscient && (
        <MultiOptionSelect
          placeholder="Select POV Character"
          variant="tertiary"
          className="p-4 pt-0"
          summaryFn={getCharacterSummary}
          collisionBoundarySelector={collisionBoundarySelector}
        >
          <POVCharacterSelect
            characters={characterNames}
            selectedCharacters={povCharacters}
            onChange={handleCharacterSelectChange}
            oneOrManySelections={oneOrManyCharacters}
          />
        </MultiOptionSelect>
      )}
    </>
  );
});
